import { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, IconButton, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useGridScrollToBottom } from "../../../core/hooks/useGridScrollToBottom";
import { useResponsive } from "../../../core/hooks/useResponsive";
import OrderHistoryCard from "./OrderHistoryCard";
import OrderLogDialog from "./OrderLogDialog";

const boolToYesNo = (v) => (v ? "Yes" : "No");

const getOrderStatusCellClassName = (value) => {
  if (!value) return "neutral-cell";
  if (value === "open") return "neutral-cell";
  if (value === "executed") return "profit-cell";
  if (value === "cancelled" || value === "rejected") return "loss-cell";
  // Also support SUCCESS/ERROR styles if those come from elsewhere.
  if (String(value).toLowerCase() === "success") return "profit-cell";
  if (String(value).toLowerCase() === "error") return "loss-cell";
  return "neutral-cell";
};

const OrderHistoryGrid = ({ orderHistory }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isCompact } = useResponsive();

  const rows = Array.isArray(orderHistory) ? orderHistory : [];
  useGridScrollToBottom(rows, "order-history-grid");

  const handleViewLogClick = (row) => {
    setSelectedOrder(row);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const columns = useMemo(
    () => [
      { field: "symbol", headerName: "Symbol", flex: 1, minWidth: 120 },
      {
        field: "orderPlaced",
        headerName: "Order Placed",
        flex: 1,
        type: "boolean",
        minWidth: 140,
        renderCell: (params) => boolToYesNo(!!params.value),
      },
      {
        field: "orderStatus",
        headerName: "Order Status",
        flex: 1.2,
        minWidth: 160,
        cellClassName: (params) => getOrderStatusCellClassName(params.value),
        renderCell: (params) => params.value || "—",
      },
      {
        field: "orderLog",
        headerName: "Order Log",
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const hasLog = Array.isArray(params.value) && params.value.length > 0;
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (hasLog) handleViewLogClick(params.row);
              }}
              disabled={!hasLog}
              sx={{
                color: hasLog ? "primary.main" : "action.disabled",
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    ],
    []
  );

  if (isCompact) {
    /* 580px of minimum column width will not fit a phone; same rows as cards. */
    return (
      <>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No orders yet.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1, maxHeight: 340, overflowY: "auto", pr: 0.25 }}>
            {rows.map((row, idx) => (
              <OrderHistoryCard key={row.symbol ?? idx} row={row} onViewLog={handleViewLogClick} />
            ))}
          </Box>
        )}
        <OrderLogDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          orderLog={selectedOrder?.orderLog || []}
          symbol={selectedOrder?.symbol}
        />
      </>
    );
  }

  return (
    <>
      <div id="order-history-grid" style={{ height: "100%", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.symbol ?? Math.random()}
          rowHeight={35}
          hideFooter
          disableRowSelectionOnClick
          sx={{ height: "100%" }}
        />
      </div>
      <OrderLogDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        orderLog={selectedOrder?.orderLog || []}
        symbol={selectedOrder?.symbol}
      />
    </>
  );
};

export default OrderHistoryGrid;

